/*
 OK FBI — Owner Settings
 Three tabs:
   1) حساب المالك    — edit own name/agent_id + change password
   2) إدارة المحققين — CRUD + permissions + assigned cases
   3) سجل النشاط     — activity log viewer
 OWNER-only + MANAGE_INVESTIGATORS gated. Guarded by RequirePerm at route level.
*/
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/pages/AuthPage";
import { Shell } from "@/components/Shell";
import { Shield, UserPlus, Users, Activity, Save, KeyRound, Trash2, Power, X, CheckCircle2, Lock, Unlock, FolderOpen, Inbox, Search } from "lucide-react";
import { toast } from "sonner";
import { PackageChip, PackagePicker, usePackageConfigs } from "@/components/PackageChip";
import { RankChip } from "@/components/RankChip";
import { SpecialTitleChip, useSpecialTitles } from "@/components/SpecialTitleChip";

const PERM_GROUPS = [
  { title: "القضايا", keys: ["VIEW_CASES", "CREATE_CASE", "EDIT_CASE", "DELETE_CASE", "DUPLICATE_CASE"] },
  { title: "الأدلة", keys: ["VIEW_EVIDENCE", "ADD_EVIDENCE", "EDIT_EVIDENCE", "DELETE_EVIDENCE", "UPLOAD_EVIDENCE_MEDIA"] },
  { title: "المشتبه بهم", keys: ["VIEW_SUSPECTS", "ADD_SUSPECT", "EDIT_SUSPECT", "DELETE_SUSPECT"] },
  { title: "الاستجواب", keys: ["VIEW_INTERROGATIONS", "ADD_INTERROGATION", "EDIT_INTERROGATION"] },
  { title: "التحليل الجنائي", keys: ["USE_FORENSIC_LAB", "EDIT_FORENSIC_RESULTS"] },
  { title: "الكاميرات (CCTV)", keys: ["VIEW_CCTV", "MANAGE_CCTV_MEDIA"] },
  { title: "غرفة العمليات و TikTok", keys: ["VIEW_OPERATIONS_ROOM", "VIEW_LIVE_COMMENTS", "START_STOP_VOTING", "CONTROL_TIKTOK_LIVE"] },
  { title: "الحل النهائي", keys: ["VIEW_FINAL_SOLUTION", "EDIT_FINAL_SOLUTION", "REVEAL_FINAL_SOLUTION"] },
  { title: "أدوات البث", keys: ["USE_CUE", "USE_OBS_CONTROLS"] },
  { title: "إدارة النظام (OWNER)", keys: ["VIEW_ACTIVITY_LOG", "MANAGE_INVESTIGATORS", "OWNER_SETTINGS"] },
];

const PERM_LABEL = {
  VIEW_CASES: "مشاهدة القضايا", CREATE_CASE: "إنشاء قضية", EDIT_CASE: "تعديل قضية",
  DELETE_CASE: "حذف قضية", DUPLICATE_CASE: "نسخ قضية",
  VIEW_EVIDENCE: "مشاهدة الأدلة", ADD_EVIDENCE: "إضافة دليل", EDIT_EVIDENCE: "تعديل دليل",
  DELETE_EVIDENCE: "حذف دليل", UPLOAD_EVIDENCE_MEDIA: "رفع ملفات الأدلة",
  VIEW_SUSPECTS: "مشاهدة المشتبه بهم", ADD_SUSPECT: "إضافة مشتبه به",
  EDIT_SUSPECT: "تعديل مشتبه به", DELETE_SUSPECT: "حذف مشتبه به",
  VIEW_INTERROGATIONS: "مشاهدة الاستجوابات", ADD_INTERROGATION: "إضافة استجواب",
  EDIT_INTERROGATION: "تعديل استجواب",
  USE_FORENSIC_LAB: "استخدام التحليل الجنائي", EDIT_FORENSIC_RESULTS: "تعديل نتائج التحليل",
  VIEW_CCTV: "مشاهدة الكاميرات", MANAGE_CCTV_MEDIA: "إدارة ملفات الكاميرات",
  VIEW_OPERATIONS_ROOM: "دخول غرفة العمليات", VIEW_LIVE_COMMENTS: "مشاهدة تعليقات البث",
  START_STOP_VOTING: "تشغيل/إغلاق التصويت", CONTROL_TIKTOK_LIVE: "التحكم في TikTok LIVE",
  VIEW_FINAL_SOLUTION: "مشاهدة الحل النهائي", EDIT_FINAL_SOLUTION: "تعديل الحل النهائي",
  REVEAL_FINAL_SOLUTION: "كشف الحل النهائي",
  USE_CUE: "استخدام CUE", USE_OBS_CONTROLS: "أدوات OBS",
  VIEW_ACTIVITY_LOG: "مشاهدة سجل النشاط", MANAGE_INVESTIGATORS: "إدارة المحققين",
  OWNER_SETTINGS: "إعدادات المالك",
};

const ROLE_AR = {
  OWNER: "المالك", ADMIN: "مدير", CASE_EDITOR: "كاتب القضايا",
  INVESTIGATOR: "محقق", VIEWER: "مشاهد",
};

export default function OwnerSettingsPage() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("profile");
  if (!user) return null;

  return (
    <Shell>
      <div dir="rtl" className="max-w-6xl mx-auto" data-testid="owner-settings-page">
        <div className="mb-6 flex items-center gap-3">
          <Shield className="text-gold" size={22} />
          <h1 className="text-2xl font-cairo text-gold tracking-widest">لوحة المالك</h1>
        </div>

        <div className="flex items-center gap-2 border-b border-metal mb-6">
          <TabBtn active={tab === "profile"} onClick={() => setTab("profile")} icon={Shield} label="حساب المالك" testid="tab-profile" />
          <TabBtn active={tab === "requests"} onClick={() => setTab("requests")} icon={Inbox} label="طلبات الانضمام" testid="tab-requests" />
          <TabBtn active={tab === "investigators"} onClick={() => setTab("investigators")} icon={Users} label="إدارة المحققين" testid="tab-investigators" />
          <TabBtn active={tab === "release"} onClick={() => setTab("release")} icon={FolderOpen} label="إتاحة القضايا" testid="tab-release" />
          <TabBtn active={tab === "live"} onClick={() => setTab("live")} icon={Activity} label="بث حي / كشف" testid="tab-live" />
          <TabBtn active={tab === "activity"} onClick={() => setTab("activity")} icon={Activity} label="سجل النشاط" testid="tab-activity" />
        </div>

        {tab === "profile" && <OwnerProfileTab user={user} onSaved={refresh} />}
        {tab === "requests" && <PendingRequestsTab />}
        {tab === "investigators" && <InvestigatorsTab currentUser={user} />}
        {tab === "release" && <CaseReleaseTab />}
        {tab === "live" && <LiveEventsTab />}
        {tab === "activity" && <ActivityLogTab />}
      </div>
    </Shell>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-[13px] font-cairo border-b-2 -mb-px transition-colors ${active ? "text-gold border-gold" : "text-zinc-400 border-transparent hover:text-white"}`}>
      <Icon size={14} /> {label}
    </button>
  );
}

// ============ Owner Profile ============
function OwnerProfileTab({ user, onSaved }) {
  return (
    <div className="grid gap-6" data-testid="owner-profile-tab">
      <OwnerIdentitySection user={user} onSaved={onSaved} />
      <LoginCredentialsSection user={user} onSaved={onSaved} />
    </div>
  );
}

// --- Section: name only (safe to change without password) ---
function OwnerIdentitySection({ user, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [busy, setBusy] = useState(false);
  const dirty = name.trim() !== (user.name || "").trim();
  const save = async () => {
    if (!name.trim()) return toast.error("الاسم مطلوب");
    setBusy(true);
    try {
      await api.patch("/users/me/profile", { name: name.trim() });
      await onSaved();
      toast.success("تم حفظ بيانات المالك");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "خطأ في الحفظ"); }
    finally { setBusy(false); }
  };
  return (
    <div className="panel gold-border p-5">
      <div className="flex items-center gap-2 mb-4">
        <Shield size={14} className="text-gold" />
        <div className="text-gold text-sm font-cairo tracking-widest">هوية المالك</div>
      </div>
      <div className="max-w-md">
        <Field label="اسم المالك" value={name} onChange={setName} testid="owner-name" />
        <button onClick={save} disabled={busy || !dirty} data-testid="owner-save-profile"
          className="mt-2 flex items-center gap-2 px-4 py-2 bg-gold text-black font-cairo text-[13px] tracking-widest disabled:opacity-40">
          <Save size={14} /> حفظ التغييرات
        </button>
      </div>
    </div>
  );
}

// --- Section: LOGIN CREDENTIALS (agent_id + password, each guarded by current password) ---
function LoginCredentialsSection({ user, onSaved }) {
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [showPwForm, setShowPwForm] = useState(false);
  return (
    <div className="panel gold-border p-5" data-testid="login-credentials-section">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound size={14} className="text-gold" />
        <div className="text-gold text-sm font-cairo tracking-widest">بيانات الدخول</div>
      </div>
      <div className="text-zinc-500 text-[11px] font-cairo mb-4 leading-relaxed">
        هذا القسم متاح لحساب OWNER فقط. تغيير رقم الدخول أو كلمة المرور يتطلب كلمة المرور الحالية للتحقق.
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Agent ID row */}
        <div className="bg-black/40 border border-zinc-800 p-4">
          <div className="text-zinc-400 text-[11px] font-cairo mb-1">رقم الدخول الحالي</div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-gold font-mono-ar text-2xl tracking-widest" data-testid="current-agent-id">
              #{user.agent_id}
            </div>
            {!showAgentForm && (
              <button onClick={() => setShowAgentForm(true)} data-testid="btn-change-agent-id"
                className="px-3 py-1.5 border border-gold text-gold font-cairo text-[11px] tracking-widest hover:bg-gold/10">
                تغيير رقم الدخول
              </button>
            )}
          </div>
          {showAgentForm && (
            <ChangeAgentIdForm user={user} onDone={() => { setShowAgentForm(false); onSaved(); }} onCancel={() => setShowAgentForm(false)} />
          )}
        </div>

        {/* Password row */}
        <div className="bg-black/40 border border-zinc-800 p-4">
          <div className="text-zinc-400 text-[11px] font-cairo mb-1">كلمة المرور</div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-gold font-mono-ar text-2xl tracking-widest">••••••••</div>
            {!showPwForm && (
              <button onClick={() => setShowPwForm(true)} data-testid="btn-change-password"
                className="px-3 py-1.5 border border-gold text-gold font-cairo text-[11px] tracking-widest hover:bg-gold/10">
                تغيير كلمة المرور
              </button>
            )}
          </div>
          {showPwForm && (
            <ChangePasswordForm onDone={() => setShowPwForm(false)} onCancel={() => setShowPwForm(false)} />
          )}
        </div>
      </div>
    </div>
  );
}

function ChangeAgentIdForm({ user, onDone, onCancel }) {
  const [newId, setNewId] = useState(user.agent_id || "");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!newId.trim()) return toast.error("رقم الدخول الجديد مطلوب");
    if (newId === user.agent_id) return toast.error("لم يتغيّر الرقم");
    if (!pw) return toast.error("كلمة المرور الحالية مطلوبة للتأكيد");
    setBusy(true);
    try {
      await api.patch("/users/me/profile", { agent_id: newId.trim(), current_password: pw });
      await onDone();
      toast.success("تم تحديث بيانات الدخول بنجاح");
    } catch (e) {
      const msg = formatErr(e?.response?.data?.detail);
      if (msg && msg.includes("كلمة المرور الحالية")) toast.error("كلمة المرور الحالية غير صحيحة");
      else toast.error(msg || "فشل التحديث");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="change-agent-id-form">
      <Field label="رقم الدخول الجديد" value={newId}
        onChange={(v) => setNewId(v.replace(/[^0-9]/g, "").slice(0, 4))}
        testid="new-agent-id" placeholder="مثال: 001" />
      <Field label="كلمة المرور الحالية للتأكيد" type="password" value={pw}
        onChange={setPw} testid="agent-id-verify-pw" />
      <div className="flex items-center gap-2">
        <button onClick={submit} disabled={busy} data-testid="save-new-agent-id"
          className="flex items-center gap-2 px-3 py-1.5 bg-gold text-black font-cairo text-[11px] tracking-widest disabled:opacity-40">
          <Save size={12} /> حفظ
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 border border-zinc-700 text-zinc-400 font-cairo text-[11px]">إلغاء</button>
      </div>
    </div>
  );
}

function ChangePasswordForm({ onDone, onCancel }) {
  const [pwOld, setPwOld] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!pwOld || !pw1 || !pw2) return toast.error("جميع الحقول مطلوبة");
    if (pw1 !== pw2) return toast.error("كلمتا المرور غير متطابقتين");
    if (pw1.length < 6) return toast.error("كلمة المرور قصيرة (6 أحرف على الأقل)");
    setBusy(true);
    try {
      await api.post("/users/me/password",
        { current_password: pwOld, new_password: pw1, confirm_password: pw2 });
      toast.success("تم تحديث بيانات الدخول بنجاح");
      onDone();
    } catch (e) {
      const msg = formatErr(e?.response?.data?.detail);
      if (msg && msg.includes("كلمة المرور الحالية")) toast.error("كلمة المرور الحالية غير صحيحة");
      else toast.error(msg || "فشل التحديث");
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="change-password-form">
      <Field label="كلمة المرور الحالية" type="password" value={pwOld} onChange={setPwOld} testid="cur-pw" />
      <Field label="كلمة المرور الجديدة" type="password" value={pw1} onChange={setPw1} testid="new-pw" />
      <Field label="تأكيد كلمة المرور" type="password" value={pw2} onChange={setPw2} testid="confirm-pw" />
      <div className="flex items-center gap-2">
        <button onClick={submit} disabled={busy} data-testid="save-new-password"
          className="flex items-center gap-2 px-3 py-1.5 bg-gold text-black font-cairo text-[11px] tracking-widest disabled:opacity-40">
          <Save size={12} /> حفظ
        </button>
        <button onClick={onCancel} className="px-3 py-1.5 border border-zinc-700 text-zinc-400 font-cairo text-[11px]">إلغاء</button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div className="mb-3">
      <label className="block text-[11px] text-zinc-400 font-cairo mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} placeholder={placeholder}
        className="w-full bg-black/60 border border-zinc-800 focus:border-gold outline-none px-3 py-2 text-[13px] font-mono-ar text-white" />
    </div>
  );
}

// ============ Investigators Management ============
function InvestigatorsTab({ currentUser }) {
  const [list, setList] = useState([]);
  const [cases, setCases] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // user obj or null (create)
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.get("/users"), api.get("/cases")]);
      setList(u.data || []); setCases(c.data || []);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Search filter — normalizes "#4662" and "4662" to the same query,
  // matches by exact/partial agent_id, and case-insensitive name.
  const filteredList = useMemo(() => {
    const raw = (search || "").trim();
    if (!raw) return list;
    // Strip leading '#' and surrounding whitespace for agent-id lookups.
    const numeric = raw.replace(/^#+/, "").trim();
    const lower = raw.toLowerCase();
    const numLower = numeric.toLowerCase();
    return list.filter((u) => {
      const aid = String(u.agent_id || "").toLowerCase();
      const name = String(u.name || "").toLowerCase();
      // Match if agent_id starts-with OR contains the numeric portion
      // (start-with is nicer UX but includes both for lenient search).
      if (numeric && aid.includes(numLower)) return true;
      if (name.includes(lower)) return true;
      return false;
    });
  }, [list, search]);

  const startCreate = () => { setEditing(null); setShowForm(true); };
  const startEdit = (u) => { setEditing(u); setShowForm(true); };
  const onSaved = async () => { setShowForm(false); await load(); };

  const del = async (u) => {
    if (!window.confirm(`حذف المحقق ${u.agent_id} - ${u.name}؟`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success("تم الحذف"); await load(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "فشل الحذف"); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.user_id}`, { active: !u.active }); toast.success(u.active ? "تم التعطيل" : "تم التفعيل"); await load(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "فشل"); }
  };

  return (
    <div data-testid="investigators-tab">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="text-zinc-400 text-[12px] font-cairo">
          إجمالي المحققين: <span className="text-gold">{list.length}</span>
          {search && (
            <span className="mr-3">
              · نتائج البحث: <span className="text-gold">{filteredList.length}</span>
            </span>
          )}
        </div>
        <button onClick={startCreate} data-testid="add-investigator-btn"
          className="flex items-center gap-2 px-4 py-2 bg-gold text-black font-cairo text-[13px] tracking-widest">
          <UserPlus size={14} /> إضافة محقق
        </button>
      </div>

      {/* Search input — normalizes '#4662' → '4662' and matches partial names. */}
      <div className="relative mb-4">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" size={16} />
        <input
          data-testid="investigators-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ابحث بالاسم أو رقم المحقق"
          className="w-full pr-10 pl-3 py-2 bg-black/40 border border-metal focus:border-gold outline-none text-white font-cairo text-[13px]"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            data-testid="investigators-search-clear"
            className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white p-1"
            aria-label="مسح البحث"
          ><X size={14} /></button>
        )}
      </div>

      <div className="panel gold-border overflow-hidden">
        <table className="w-full text-[12px]" data-testid="investigators-table">
          <thead className="bg-black/50 text-gold font-cairo">
            <tr>
              <th className="text-right p-3">#</th>
              <th className="text-right p-3">الاسم</th>
              <th className="text-right p-3">الوظيفة</th>
              <th className="text-right p-3">الباقة</th>
              <th className="text-right p-3">الرتبة</th>
              <th className="text-right p-3">المستوى</th>
              <th className="text-right p-3">اللقب الخاص</th>
              <th className="text-right p-3">الدور</th>
              <th className="text-right p-3">الحالة</th>
              <th className="text-right p-3">آخر دخول</th>
              <th className="text-right p-3">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.length === 0 && (
              <tr data-testid="investigators-search-empty">
                <td colSpan={11} className="p-6 text-center text-zinc-500 font-cairo">
                  {search ? "لا يوجد محقق مطابق" : "لا يوجد محققون"}
                </td>
              </tr>
            )}
            {filteredList.map((u) => (
              <tr key={u.user_id} className="border-t border-metal hover:bg-white/5" data-testid={`user-row-${u.agent_id}`}>
                <td className="p-3 font-mono-ar">
                  {u.user_id ? (
                    <Link to={`/investigator/${u.user_id}`} className="text-white hover:text-gold" data-testid={`user-agent-link-${u.agent_id}`}>
                      #{u.agent_id}
                    </Link>
                  ) : (
                    <span className="text-white">#{u.agent_id}</span>
                  )}
                </td>
                <td className="p-3 font-cairo">
                  {u.user_id ? (
                    <Link to={`/investigator/${u.user_id}`} className="text-white hover:text-gold" data-testid={`user-name-link-${u.agent_id}`}>
                      {u.name}
                    </Link>
                  ) : (
                    <span className="text-white">{u.name}</span>
                  )}
                </td>
                <td className="p-3 text-zinc-400 font-cairo">{u.job_title || "-"}</td>
                <td className="p-3">
                  {u.role === "OWNER"
                    ? <span className="px-2 py-0.5 text-[10px] font-cairo border border-gold text-gold rounded-full">المالك</span>
                    : <PackageChip plan={u.membership_plan || "FREE"} size="sm"/>}
                </td>
                <td className="p-3">
                  {u.role === "OWNER"
                    ? <span className="text-[10px] font-cairo text-gold">القيادة</span>
                    : <RankChip xp={u.points || 0} size="sm"/>}
                </td>
                <td className="p-3 text-white font-mono-ar text-[11px]">
                  {u.role === "OWNER"
                    ? "∞"
                    : (u.level ?? 1)}
                </td>
                <td className="p-3">
                  {u.special_title_id
                    ? <SpecialTitleChip titleId={u.special_title_id} size="sm"/>
                    : <span className="text-zinc-600 text-[10px] font-cairo">—</span>}
                </td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 text-[10px] font-cairo border ${u.role === "OWNER" ? "border-gold text-gold" : "border-zinc-700 text-zinc-300"}`}>
                    {ROLE_AR[u.role] || u.role}
                  </span>
                </td>
                <td className="p-3">
                  {u.active
                    ? <span className="text-emerald-400 text-[11px] font-cairo">● نشط</span>
                    : <span className="text-red-400 text-[11px] font-cairo">● موقوف</span>}
                </td>
                <td className="p-3 text-zinc-500 text-[10px] font-mono-ar">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString("ar-EG") : "—"}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(u)} title="تعديل"
                      data-testid={`edit-user-${u.agent_id}`}
                      className="p-1 text-zinc-400 hover:text-gold">
                      <Save size={13} />
                    </button>
                    {u.role !== "OWNER" && (
                      <>
                        <button onClick={() => toggleActive(u)} title={u.active ? "تعطيل" : "تفعيل"}
                          data-testid={`toggle-user-${u.agent_id}`}
                          className="p-1 text-zinc-400 hover:text-amber-400">
                          <Power size={13} />
                        </button>
                        <button onClick={() => del(u)} title="حذف"
                          data-testid={`delete-user-${u.agent_id}`}
                          className="p-1 text-zinc-400 hover:text-red-400">
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && !loading && (
              <tr><td colSpan={10} className="p-6 text-center text-zinc-500 font-cairo">لا يوجد محققون</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <InvestigatorFormModal user={editing} currentUser={currentUser} cases={cases}
          onClose={() => setShowForm(false)} onSaved={onSaved} />
      )}
    </div>
  );
}

// ============ Investigator Form Modal ============
function InvestigatorFormModal({ user, currentUser, cases, onClose, onSaved }) {
  const isEdit = !!user;
  const isOwnerTarget = user?.role === "OWNER";
  const titles = useSpecialTitles();
  const { refresh: refreshAuth, user: sessionUser } = useAuth();
  const [form, setForm] = useState({
    agent_id: user?.agent_id || "",
    name: user?.name || "",
    job_title: user?.job_title || "",
    role: user?.role || "INVESTIGATOR",
    active: user?.active ?? true,
    password: "",
    confirm_password: "",
    permissions: user?.permissions || {},
    assigned_cases: user?.assigned_cases || "all",
    membership_plan: user?.membership_plan || "FREE",
    special_title_id: user?.special_title_id || "",
  });
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState({ role_defaults: {}, roles: [] });
  // Live agent-id availability (debounced). OWNER may claim any unused number,
  // including reserved digits (1..9, 11..99, 111..999, 1111..9999).
  const [idCheck, setIdCheck] = useState({ status: "idle", reason: "" });

  useEffect(() => {
    api.get("/permissions-catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  // Debounced availability lookup — hits new /users/agent-id-availability endpoint.
  useEffect(() => {
    const aid = String(form.agent_id || "").trim();
    if (!aid) { setIdCheck({ status: "idle", reason: "" }); return; }
    // Same as current — no need to hit the server.
    if (isEdit && aid === (user?.agent_id || "")) {
      setIdCheck({ status: "same", reason: "" }); return;
    }
    setIdCheck({ status: "checking", reason: "" });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/users/agent-id-availability", {
          params: { agent_id: aid, exclude_user_id: user?.user_id || "" },
        });
        if (data.available) setIdCheck({ status: "available", reason: "" });
        else setIdCheck({ status: "unavailable", reason: data.reason || "" });
      } catch {
        setIdCheck({ status: "idle", reason: "" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.agent_id, isEdit, user?.agent_id, user?.user_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyRoleDefaults = (role) => {
    const defaults = catalog.role_defaults?.[role] || {};
    setForm((f) => ({ ...f, role, permissions: { ...defaults } }));
  };

  const togglePerm = (k) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [k]: !f.permissions[k] } }));

  const save = async () => {
    if (!form.agent_id || !form.name) return toast.error("الاسم والرقم مطلوبان");
    if (!isEdit && !form.password) return toast.error("كلمة المرور مطلوبة");
    if (form.password && form.password !== form.confirm_password) return toast.error("كلمتا المرور غير متطابقتين");
    setBusy(true);
    try {
      const payload = {
        agent_id: form.agent_id, name: form.name, job_title: form.job_title,
        role: form.role, active: form.active, permissions: form.permissions,
        assigned_cases: form.assigned_cases,
        membership_plan: form.membership_plan,
      };
      if (isEdit) {
        await api.patch(`/users/${user.user_id}`, payload);
        if (form.password) {
          await api.post(`/users/${user.user_id}/password`, { new_password: form.password, confirm_password: form.confirm_password });
        }
        // Special title is a separate axis — update via its dedicated endpoint
        // ONLY when the OWNER actually changed it (avoid 403 for non-owner edits).
        const originalTitle = user?.special_title_id || "";
        if (!isOwnerTarget && (form.special_title_id || "") !== originalTitle) {
          try {
            await api.patch(`/owner/users/${user.user_id}/special-title`, {
              title_id: form.special_title_id || null,
            });
          } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail) || "فشل حفظ اللقب الخاص");
          }
        }
        // If editing the currently logged-in user, refresh auth so
        // navigation/permissions reflect the change without logout.
        if (sessionUser?.user_id === user.user_id) {
          try { await refreshAuth(); } catch {}
        }
        toast.success("تم حفظ بيانات وصلاحيات المستخدم بنجاح");
      } else {
        payload.password = form.password;
        await api.post("/users", payload);
        toast.success("تم إنشاء المحقق");
      }
      onSaved();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "فشل الحفظ"); }
    finally { setBusy(false); }
  };

  const allSelected = form.assigned_cases === "all";
  const selectedList = Array.isArray(form.assigned_cases) ? form.assigned_cases : [];

  const toggleCase = (cid) => {
    if (allSelected) return set("assigned_cases", cases.filter(c => c.id !== cid).map(c => c.id));
    if (selectedList.includes(cid)) set("assigned_cases", selectedList.filter(x => x !== cid));
    else set("assigned_cases", [...selectedList, cid]);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="investigator-form-modal">
      <div className="bg-[#0b0b0e] border border-gold/40 max-w-3xl w-full max-h-[90vh] flex flex-col relative" onClick={(e) => e.stopPropagation()}>
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-[#0b0b0e]/95 backdrop-blur-sm border-b border-metal px-6 py-4 flex items-center justify-between">
          <div className="text-gold text-lg font-cairo tracking-widest">{isEdit ? "تعديل المحقق" : "إضافة محقق جديد"}</div>
          <button onClick={onClose} data-testid="close-investigator-form" className="text-zinc-500 hover:text-white"><X size={16} /></button>
        </div>
        {/* Scrollable body */}
        <div className="overflow-y-auto p-6 flex-1">
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-[11px] text-zinc-400 font-cairo mb-1">رقم المحقق</label>
            <div className="relative">
              <input
                type="text" inputMode="numeric" placeholder="007"
                value={form.agent_id}
                onChange={(e) => set("agent_id", e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                data-testid="form-agent-id"
                className="w-full bg-black/60 border border-zinc-800 focus:border-gold px-3 py-2 pl-9 text-[13px] font-cairo text-white outline-none"
                dir="ltr" style={{ textAlign: "right" }}
              />
              {/* live availability indicator (OWNER visible feedback) */}
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[16px] leading-none"
                    data-testid="agent-id-status" data-status={idCheck.status}>
                {idCheck.status === "available" && <span className="text-emerald-400" title="الرقم متاح">✓</span>}
                {idCheck.status === "same"      && <span className="text-zinc-500" title="نفس الرقم الحالي">·</span>}
                {idCheck.status === "checking"  && <span className="text-zinc-500 animate-pulse">…</span>}
                {idCheck.status === "unavailable" && <span className="text-red-400" title="غير متاح">✕</span>}
              </span>
            </div>
            <div className="text-[10px] font-cairo mt-1 h-4">
              {idCheck.status === "available" && (
                <span className="text-emerald-400" data-testid="agent-id-msg-available">✓ الرقم متاح</span>
              )}
              {idCheck.status === "unavailable" && idCheck.reason === "USED_BY_OTHER" && (
                <span className="text-red-400"    data-testid="agent-id-msg-used">✕ الرقم مستخدم بالفعل</span>
              )}
              {idCheck.status === "unavailable" && idCheck.reason === "RESERVED" && (
                <span className="text-red-400"    data-testid="agent-id-msg-reserved">هذا الرقم محجوز للإدارة</span>
              )}
              {idCheck.status === "unavailable" && idCheck.reason === "INVALID" && (
                <span className="text-red-400"    data-testid="agent-id-msg-invalid">رقم غير صالح</span>
              )}
            </div>
          </div>
          <Field label="اسم المحقق" value={form.name} onChange={(v) => set("name", v)} testid="form-name" placeholder="خالد" />
          <Field label="الوظيفة" value={form.job_title} onChange={(v) => set("job_title", v)} testid="form-job" placeholder="كاتب القضايا" />
          <div>
            <label className="block text-[11px] text-zinc-400 font-cairo mb-1">الدور</label>
            <select value={form.role} onChange={(e) => applyRoleDefaults(e.target.value)}
              data-testid="form-role"
              disabled={isOwnerTarget}
              className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[13px] font-cairo text-white">
              {(catalog.roles || []).map(r => (
                <option key={r} value={r} disabled={r === "OWNER" && currentUser.role !== "OWNER"}>
                  {ROLE_AR[r] || r}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-[11px] text-zinc-400 font-cairo mb-2">الباقة الحالية</label>
            <PackagePicker value={form.membership_plan}
                           onChange={(k) => set("membership_plan", k)}
                           disabled={isOwnerTarget}/>
            <div className="text-[9px] text-zinc-500 font-cairo mt-1">
              تغيير الباقة يطبّق صلاحياتها تلقائياً — لا حاجة لضبط "صلاحيات متقدمة" يدوياً.
            </div>
          </div>
          {!isOwnerTarget && (
            <div className="col-span-2">
              <label className="block text-[11px] text-zinc-400 font-cairo mb-2">
                اللقب الخاص (يُعطى يدوياً من OWNER — لا يغيّر الباقة أو الرتبة أو الصلاحيات)
              </label>
              <div className="flex items-center gap-3 flex-wrap">
                <select value={form.special_title_id || ""}
                        onChange={(e) => set("special_title_id", e.target.value)}
                        data-testid="form-special-title"
                        className="min-w-[220px] bg-black/60 border border-zinc-800 px-3 py-2 text-[13px] font-cairo text-white">
                  <option value="">بدون لقب خاص</option>
                  {titles.filter(t => t.enabled !== false).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {form.special_title_id && (
                  <SpecialTitleChip titleId={form.special_title_id} size="sm"/>
                )}
                <a href="/host/progression?tab=titles" target="_blank" rel="noreferrer"
                   className="text-[10px] font-cairo text-yellow-500 hover:underline">
                  + إنشاء لقب جديد
                </a>
              </div>
            </div>
          )}
          <Field label={isEdit ? "كلمة مرور جديدة (اختياري)" : "كلمة المرور"} type="password"
            value={form.password} onChange={(v) => set("password", v)} testid="form-password" />
          <Field label="تأكيد كلمة المرور" type="password" value={form.confirm_password}
            onChange={(v) => set("confirm_password", v)} testid="form-confirm-password" />
          <div className="col-span-2 flex items-center gap-2 pt-1">
            <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)}
              data-testid="form-active" disabled={isOwnerTarget}
              className="w-4 h-4 accent-yellow-600" id="active-cb" />
            <label htmlFor="active-cb" className="text-[13px] font-cairo text-zinc-300">
              حساب نشط (مسموح بتسجيل الدخول)
            </label>
          </div>
        </div>

        {/* Assigned Cases */}
        <div className="border-t border-metal pt-4 mb-4">
          <div className="text-gold text-[13px] font-cairo tracking-widest mb-2">القضايا المسموحة</div>
          <div className="flex items-center gap-4 mb-2">
            <label className="flex items-center gap-2 text-[12px] text-zinc-300 font-cairo">
              <input type="radio" checked={allSelected} onChange={() => set("assigned_cases", "all")}
                data-testid="assigned-all" className="accent-yellow-600" />
              جميع القضايا (بما فيها المستقبلية)
            </label>
            <label className="flex items-center gap-2 text-[12px] text-zinc-300 font-cairo">
              <input type="radio" checked={!allSelected} onChange={() => set("assigned_cases", selectedList)}
                data-testid="assigned-specific" className="accent-yellow-600" />
              قضايا محددة فقط
            </label>
          </div>
          {!allSelected && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
              {cases.map(c => (
                <label key={c.id} className="flex items-center gap-2 text-[11px] text-zinc-300 font-cairo bg-black/40 border border-zinc-800 px-2 py-1.5">
                  <input type="checkbox" checked={selectedList.includes(c.id)}
                    onChange={() => toggleCase(c.id)}
                    data-testid={`case-${c.id}`}
                    className="w-3.5 h-3.5 accent-yellow-600" />
                  <span className="text-gold font-mono-ar">{c.code}</span>
                  <span className="text-zinc-400">— {c.title}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Advanced permissions — collapsed by default */}
        <details className="border-t border-metal pt-4 mb-4" data-testid="advanced-perms">
          <summary className="cursor-pointer text-gold text-[13px] font-cairo tracking-widest mb-2 select-none list-none flex items-center gap-2">
            <span className="text-yellow-400">▸</span> صلاحيات متقدمة
            <span className="text-[10px] text-neutral-500">(اختياري — الباقة تحدّد الصلاحيات تلقائياً)</span>
          </summary>
          <div className="grid md:grid-cols-2 gap-3 mt-3">
            {PERM_GROUPS.map(g => (
              <div key={g.title} className="bg-black/30 border border-zinc-800 p-3">
                <div className="text-gold/80 font-cairo text-[12px] mb-2">{g.title}</div>
                <div className="space-y-1">
                  {g.keys.map(k => (
                    <label key={k} className="flex items-center gap-2 text-[11px] text-zinc-300 font-cairo">
                      <input type="checkbox" checked={!!form.permissions[k]}
                        onChange={() => togglePerm(k)}
                        data-testid={`perm-${k}`}
                        disabled={isOwnerTarget}
                        className="w-3.5 h-3.5 accent-yellow-600" />
                      {PERM_LABEL[k] || k}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </details>

        </div>
        {/* Sticky footer — always visible; primary button uses the OK FBI gold
            primary-button style with a strong hover/focus ring so it cannot
            look disabled at rest. */}
        <div className="sticky bottom-0 z-10 bg-[#0b0b0e]/95 backdrop-blur-sm border-t border-metal px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          {/* Left: real blocking reason for OWNER (never silently disables) */}
          <div className="text-[11px] font-cairo text-red-400 min-h-4 flex-1" data-testid="save-block-reason">
            {idCheck.status === "unavailable" && idCheck.reason === "USED_BY_OTHER" && "الرقم مستخدم بالفعل — اختر رقماً غير مستخدم"}
            {idCheck.status === "unavailable" && idCheck.reason === "RESERVED"      && "هذا الرقم محجوز للإدارة"}
            {idCheck.status === "unavailable" && idCheck.reason === "INVALID"       && "رقم غير صالح — استخدم أرقاماً فقط"}
          </div>
          <div className="flex items-center gap-2 justify-end">
            <button onClick={onClose}
                    data-testid="cancel-investigator"
                    className="px-4 py-2.5 text-[12px] text-zinc-300 border border-zinc-700 hover:border-zinc-500 font-cairo tracking-widest">
              إلغاء
            </button>
            <button onClick={save}
              disabled={busy || idCheck.status === "unavailable"}
              data-testid="save-investigator"
              className="flex items-center gap-2 px-6 py-2.5 font-cairo text-[13px] tracking-widest transition-colors
                         bg-gold text-black font-black
                         hover:bg-gold-bright
                         focus:outline-none focus:ring-2 focus:ring-yellow-500/60
                         disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                boxShadow: (busy || idCheck.status === "unavailable")
                  ? "none"
                  : "0 4px 18px -6px rgba(232,208,138,0.55), 0 0 0 1px rgba(184,138,66,0.6)"
              }}>
              <CheckCircle2 size={14} /> {isEdit ? "حفظ التعديلات" : "حفظ المحقق"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Pending Join Requests ============
function PendingRequestsTab() {
  const [rows, setRows] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeReq, setActiveReq] = useState(null);
  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([api.get("/users/pending"), api.get("/cases")]);
      setRows(p.data || []); setCases(c.data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const reject = async (u) => {
    if (!window.confirm(`رفض طلب @${u.tiktok_username}؟`)) return;
    try { await api.post(`/users/${u.user_id}/reject`); toast.success("تم رفض الطلب"); await load(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "فشل"); }
  };
  return (
    <div data-testid="pending-requests-tab">
      <div className="text-zinc-400 text-[12px] font-cairo mb-4">
        طلبات الانضمام الجديدة: <span className="text-gold">{rows.length}</span>
      </div>
      <div className="panel gold-border overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-black/50 text-gold font-cairo">
            <tr>
              <th className="text-right p-3">TikTok</th>
              <th className="text-right p-3">الاسم</th>
              <th className="text-right p-3">النوع</th>
              <th className="text-right p-3">التاريخ</th>
              <th className="text-right p-3">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(u => (
              <tr key={u.user_id} className="border-t border-metal" data-testid={`pending-row-${u.tiktok_username}`}>
                <td className="p-3 text-gold font-mono-ar">@{u.tiktok_username}</td>
                <td className="p-3 text-white font-cairo">{u.name}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 text-[10px] font-cairo border ${u.account_type==="STREAMER"?"border-purple-700 text-purple-300":"border-zinc-700 text-zinc-300"}`}>
                    {u.account_type === "STREAMER" ? "ستريمر" : "محقق"}
                  </span>
                </td>
                <td className="p-3 text-zinc-500 text-[10px] font-mono-ar">
                  {u.created_at ? new Date(u.created_at).toLocaleString("ar-EG") : "—"}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setActiveReq({ user: u, cases })} data-testid={`approve-${u.tiktok_username}`}
                      className="px-3 py-1 bg-gold text-black font-cairo text-[11px]">قبول</button>
                    <button onClick={() => reject(u)} data-testid={`reject-${u.tiktok_username}`}
                      className="px-3 py-1 border border-red-800 text-red-300 font-cairo text-[11px]">رفض</button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-zinc-500 font-cairo">{loading?"جاري التحميل...":"لا توجد طلبات معلّقة"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {activeReq && (
        <ApproveModal req={activeReq}
          onClose={() => setActiveReq(null)}
          onDone={async () => { setActiveReq(null); await load(); }} />
      )}
    </div>
  );
}

function ApproveModal({ req, onClose, onDone }) {
  const [aid, setAid] = useState("");
  const [cases, setCases] = useState(req.user.account_type === "STREAMER" ? "all" : "all");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!aid.trim()) return toast.error("Agent ID مطلوب");
    setBusy(true);
    try {
      await api.post(`/users/${req.user.user_id}/approve`, {
        agent_id: aid.trim(),
        assigned_cases: cases,
      });
      toast.success("تم قبول الطلب");
      await onDone();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "فشل"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4" onClick={onClose}>
      <div dir="rtl" className="bg-[#0b0b0e] border border-gold/40 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="approve-modal">
        <div className="text-gold text-sm font-cairo tracking-widest mb-4">
          قبول @{req.user.tiktok_username} — {req.user.account_type === "STREAMER" ? "ستريمر" : "محقق"}
        </div>
        <Field label="Agent ID (رقم الدخول)" value={aid}
          onChange={(v) => setAid(v.replace(/[^0-9]/g, "").slice(0, 4))}
          testid="approve-agent-id" placeholder="مثال: 020" />
        <div className="text-[11px] text-zinc-400 font-cairo mb-2">القضايا المسموحة</div>
        <div className="flex items-center gap-3 mb-4">
          <label className="flex items-center gap-1 text-[12px] text-zinc-300 font-cairo">
            <input type="radio" checked={cases==="all"} onChange={() => setCases("all")}
              data-testid="approve-cases-all" className="accent-yellow-600" /> جميع القضايا
          </label>
          <label className="flex items-center gap-1 text-[12px] text-zinc-300 font-cairo">
            <input type="radio" checked={Array.isArray(cases)&&cases.length===0} onChange={() => setCases([])}
              data-testid="approve-cases-none" className="accent-yellow-600" /> لا شيء (يحددها لاحقاً)
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={submit} disabled={busy || !aid} data-testid="approve-confirm"
            className="flex-1 py-2 bg-gold text-black font-cairo text-[12px] tracking-widest disabled:opacity-40">
            {busy?"...":"قبول وتفعيل"}
          </button>
          <button onClick={onClose} className="px-4 py-2 border border-zinc-700 text-zinc-400 font-cairo text-[12px]">إلغاء</button>
        </div>
      </div>
    </div>
  );
}

// ============ Case Release Control ============
const CASE_STATUS_LIST = [
  { key: "HIDDEN",               label: "مخفية",         color: "text-zinc-400",  border: "border-zinc-700",  bg: "bg-zinc-900/40" },
  { key: "UPCOMING_LOCKED",      label: "قادمة (مقفلة)", color: "text-blue-300",  border: "border-blue-800",  bg: "bg-blue-950/40" },
  { key: "ACTIVE_INVESTIGATION", label: "قيد التحقيق",   color: "text-emerald-300", border: "border-emerald-700", bg: "bg-emerald-950/40" },
  { key: "LIVE_FINAL_PENDING",   label: "بث نهائي",       color: "text-amber-300", border: "border-amber-700", bg: "bg-amber-950/40" },
  { key: "REVEALED_ARCHIVE",     label: "مكشوفة (أرشيف)", color: "text-gold",      border: "border-gold",      bg: "bg-yellow-950/40" },
];

function CaseReleaseTab() {
  const [state, setState] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(null); // { cid, status }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/case-release");
      setState(data || {});
    } catch { setState({}); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const applyStatus = async (cid, status) => {
    try {
      await api.patch(`/case-release/${cid}`, { status });
      toast.success("تم تحديث دورة حياة القضية");
      setConfirming(null);
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "فشل التحديث"); }
  };

  const rows = Object.values(state).sort((a, b) => (a.code || "").localeCompare(b.code || ""));

  return (
    <div data-testid="case-release-tab">
      <div className="text-zinc-400 text-[12px] font-cairo mb-4 leading-relaxed">
        اختر حالة كل قضية عبر دورة الحياة الخماسية. المالك يرى الكل، والمحققون يرون حسب الحالة.
        <br />
        <span className="text-zinc-500 text-[11px]">
          مخفية → قادمة → قيد التحقيق → بث نهائي → مكشوفة (أرشيف).
        </span>
      </div>

      <div className="grid gap-3" data-testid="case-lifecycle-list">
        {rows.map((r) => {
          const curr = CASE_STATUS_LIST.find(s => s.key === r.status) || CASE_STATUS_LIST[0];
          return (
            <div key={r.case_id} className="panel p-4 flex flex-col md:flex-row md:items-center gap-3"
                 data-testid={`release-row-${r.case_id}`}>
              <div className="min-w-[220px]">
                <div className="text-white font-cairo text-sm">{r.title}</div>
                <div className="text-gold text-[10px] font-mono-ar">{r.code}</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`inline-block px-2 py-0.5 text-[10px] font-cairo border ${curr.border} ${curr.color} ${curr.bg}`}
                       data-testid={`case-current-status-${r.case_id}`}>
                    {curr.label}
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/cases/${r.case_id}/toggle-lock`);
                        toast.success(data.is_locked ? "🔒 تم قفل القضية (قريباً)" : "🔓 تم فتح القضية وإتاحتها للجميع");
                        await load();
                      } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "فشل التبديل"); }
                    }}
                    data-testid={`quick-toggle-lock-${r.case_id}`}
                    className={`px-2 py-0.5 text-[10px] font-cairo border rounded flex items-center gap-1 transition-all ${
                      r.is_locked
                        ? "bg-amber-950/80 border-amber-600 text-amber-300 hover:bg-amber-900"
                        : "bg-emerald-950/80 border-emerald-600 text-emerald-300 hover:bg-emerald-900"
                    }`}
                    title={r.is_locked ? "مقفلة — اضغط للإتاحة" : "متاحة الآن — اضغط للقفل"}
                  >
                    {r.is_locked ? <Lock size={10} className="text-amber-400" /> : <Unlock size={10} className="text-emerald-400" />}
                    <span>{r.is_locked ? "🔒 مقفلة — قريباً" : "🔓 متاحة الآن"}</span>
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 flex-1">
                {CASE_STATUS_LIST.map((s) => {
                  const active = r.status === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => {
                        if (active) return;
                        // Confirm for destructive transitions (reveal)
                        if (s.key === "REVEALED_ARCHIVE") {
                          setConfirming({ cid: r.case_id, status: s.key, title: r.title });
                        } else {
                          applyStatus(r.case_id, s.key);
                        }
                      }}
                      data-testid={`status-btn-${r.case_id}-${s.key}`}
                      className={`px-2.5 py-1 text-[11px] font-cairo border transition-colors ${
                        active
                          ? `${s.border} ${s.color} ${s.bg}`
                          : "border-metal text-zinc-500 hover:border-gold hover:text-gold"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-zinc-500 font-mono-ar text-[10px] shrink-0 min-w-[120px] text-left">
                {r.updated_at ? new Date(r.updated_at).toLocaleString("ar-EG") : "—"}
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="text-center py-6 text-zinc-500 font-cairo">{loading ? "جاري التحميل..." : "لا توجد قضايا"}</div>
        )}
      </div>

      {confirming && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
             onClick={() => setConfirming(null)} data-testid="reveal-confirm-modal">
          <div className="bg-[#0b0b0e] border border-gold p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-gold text-[10px] tracking-[0.4em] mb-2">CONFIRM REVEAL</div>
            <div className="font-cairo text-white text-lg mb-2">تأكيد كشف القضية</div>
            <div className="text-sm text-zinc-400 font-cairo mb-4">
              أنت على وشك نقل «{confirming.title}» إلى حالة «مكشوفة (أرشيف)». هذا الإجراء يفتح الحل النهائي لجميع المحققين.
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirming(null)}
                className="px-3 py-2 text-xs font-cairo border border-metal text-zinc-300 hover:text-white">
                إلغاء
              </button>
              <button onClick={() => applyStatus(confirming.cid, confirming.status)}
                data-testid="confirm-reveal-btn"
                className="btn-primary px-4 py-2 text-xs">
                نعم، اكشف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Activity Log ============
function ActivityLogTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get("/activity-log?limit=200")
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="panel gold-border overflow-hidden" data-testid="activity-log-tab">
      <table className="w-full text-[12px]">
        <thead className="bg-black/50 text-gold font-cairo">
          <tr>
            <th className="text-right p-3">الوقت</th>
            <th className="text-right p-3">المستخدم</th>
            <th className="text-right p-3">العملية</th>
            <th className="text-right p-3">الهدف</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r._id} className="border-t border-metal">
              <td className="p-3 text-zinc-500 font-mono-ar text-[10px]">{new Date(r.at).toLocaleString("ar-EG")}</td>
              <td className="p-3 text-white font-cairo">
                <span className="text-gold font-mono-ar">#{r.actor_agent_id || "?"}</span> — {r.actor_name || "?"}
              </td>
              <td className="p-3 text-emerald-300 font-mono-ar">{r.action}</td>
              <td className="p-3 text-zinc-400 font-mono-ar text-[11px]">{r.target || "—"}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={4} className="p-6 text-center text-zinc-500 font-cairo">{loading ? "جاري التحميل..." : "لا يوجد نشاط بعد"}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// -------- OWNER Live Events + Cinematic Reveal --------
const EVENT_KINDS = [
  { key: "LAB_RESULT",      label: "وصلت نتيجة المختبر" },
  { key: "NEW_WITNESS",     label: "ظهر شاهد جديد" },
  { key: "AUDIO_RECORDING", label: "وصل تسجيل صوتي" },
  { key: "NEW_EVIDENCE",    label: "تم العثور على دليل جديد" },
  { key: "CAMERA_FOOTAGE",  label: "وصلت لقطة كاميرا" },
  { key: "PHONE_CALL",      label: "ورد اتصال جديد" },
  { key: "TIMELINE_UPDATE", label: "تم تحديث الخط الزمني" },
  { key: "FILE_OPENED",     label: "فتح ملف جديد" },
];

const REVEAL_STEPS = [
  "قفل التصويت", "الملف السرّي", "المشتبه بهم", "الأدلة الحاسمة",
  "الخط الزمني", "التناقضات", "الدافع", "الجاني",
  "الشرح النهائي", "نتائج المحققين",
];

function LiveEventsTab() {
  const [cases, setCases] = useState([]);
  const [caseId, setCaseId] = useState("");
  const [events, setEvents] = useState([]);
  const [reveal, setReveal] = useState({ active: false, step: -1 });
  const [kind, setKind] = useState("LAB_RESULT");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/cases").then(({ data }) => {
      setCases(data || []);
      if ((data || [])[0]) setCaseId(data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!caseId) return;
    const load = async () => {
      const [ev, rv] = await Promise.all([
        api.get("/live-events", { params: { case_id: caseId } }).catch(() => ({ data: [] })),
        api.get("/reveal/state", { params: { case_id: caseId } }).catch(() => ({ data: {} })),
      ]);
      setEvents((ev.data || []).slice().reverse());
      setReveal((rv.data || {}).reveal || { active: false, step: -1 });
    };
    load();
    const int = setInterval(load, 4000);
    return () => clearInterval(int);
  }, [caseId]);

  const push = async () => {
    if (!caseId || !kind || busy) return;
    setBusy(true);
    try {
      await api.post("/owner/live-events", { case_id: caseId, kind, body: body || undefined });
      toast.success("تم إرسال الحدث");
      setBody("");
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الإرسال"); }
    finally { setBusy(false); }
  };

  const triggerStep = async (step) => {
    if (!caseId) return;
    if (step === 7 && !window.confirm("خطوة كشف الجاني: سيتم قفل التصويت واحتساب النقاط. متابعة؟")) return;
    setBusy(true);
    try {
      await api.post("/owner/reveal/trigger", { case_id: caseId, step });
      toast.success(`تم تشغيل الخطوة ${step >= 0 ? step + 1 : "إغلاق"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل"); }
    finally { setBusy(false); }
  };

  return (
    <div dir="rtl" data-testid="owner-live-events">
      <div className="mb-4">
        <label className="text-[11px] text-zinc-400 font-cairo block mb-1">القضية</label>
        <select value={caseId} onChange={(e) => setCaseId(e.target.value)}
          data-testid="live-case-select"
          className="bg-black/40 border border-metal p-2 text-white text-sm font-cairo w-full md:w-auto min-w-[300px] focus:border-gold outline-none">
          {cases.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
      </div>

      <div className="panel p-4 mb-4">
        <div className="text-gold font-cairo text-sm mb-3">إرسال حدث حي</div>
        <div className="flex flex-col md:flex-row gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)}
            data-testid="event-kind"
            className="bg-black/40 border border-metal p-2 text-white text-sm font-cairo min-w-[220px] focus:border-gold outline-none">
            {EVENT_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
          <input value={body} onChange={(e) => setBody(e.target.value)}
            placeholder="نص إضافي (اختياري)…"
            data-testid="event-body"
            className="flex-1 bg-black/40 border border-metal p-2 text-white text-sm font-cairo focus:border-gold outline-none" />
          <button onClick={push} disabled={busy}
            data-testid="push-live-event"
            className="btn-primary text-xs px-4 py-2">إرسال</button>
        </div>
      </div>

      <div className="panel p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-gold font-cairo text-sm">الكشف النهائي السينمائي</div>
          <div className="text-[11px] font-cairo text-zinc-500">
            {reveal.active ? `نشط — الخطوة: ${(reveal.step ?? 0) + 1}` : "غير نشط"}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-1.5">
          {REVEAL_STEPS.map((label, i) => {
            const active = reveal.step === i && reveal.active;
            return (
              <button key={i} onClick={() => triggerStep(i)}
                disabled={busy}
                data-testid={`reveal-step-${i}`}
                className={`text-[11px] font-cairo px-2 py-1.5 border transition-colors ${
                  active ? "border-gold text-gold bg-yellow-950/40" : "border-metal text-zinc-400 hover:border-gold hover:text-gold"
                }`}>
                {i + 1}. {label}
              </button>
            );
          })}
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={() => triggerStep(-1)} disabled={busy}
            data-testid="close-reveal"
            className="text-[11px] font-cairo px-3 py-1.5 border border-red-800 text-red-300 hover:bg-red-950/40">
            إغلاق الكشف
          </button>
        </div>
      </div>

      <div className="panel p-4">
        <div className="text-gold font-cairo text-sm mb-3">آخر الأحداث ({events.length})</div>
        {events.length === 0 ? (
          <div className="text-zinc-500 font-cairo text-sm text-center py-4">لا توجد أحداث بعد</div>
        ) : (
          <div className="space-y-2">
            {events.slice(0, 20).map((e) => (
              <div key={e.event_id} className="flex justify-between items-start border border-metal p-2.5">
                <div>
                  <div className="text-white font-cairo text-sm">{e.title}</div>
                  {e.body && <div className="text-[11px] text-zinc-400 font-cairo mt-0.5">{e.body}</div>}
                </div>
                <div className="text-[10px] text-zinc-500 font-mono-ar shrink-0">
                  {new Date(e.created_at).toLocaleTimeString("ar-EG")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

